import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ResourceCategory } from './resource-category';
export { ResourceCategory };
import { resourceCategorySchema } from '@schemas/business-objects/resource-category';
import { ResourceCategoryItemFormArray } from './resource-category-item-form-group';
import { ResourceSetFormGroup } from './resource-set-form-group';
import type { Resource } from './resource';
import type { TextBox } from './text-box';
import type { User } from './user';

export class ResourceCategoryFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    passCriteria: SchemaFormControl;
    sequence: SchemaFormControl;
    locked: SchemaFormControl;
    items: ResourceCategoryItemFormArray;
    resourceSetId: SchemaFormControl;
    resourceSet: ResourceSetFormGroup;
  };

  declare value: Partial<ResourceCategory>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(resourceCategorySchema, ResourceCategory, data, options);
  }

  get object(): ResourceCategory {
    return new ResourceCategory(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'items':
        return new ResourceCategoryItemFormArray();
      case 'resourceSet':
        return new ResourceSetFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ResourceCategory[]> {
    return ResourceCategory.find(filter);
  }

  static async findOne(filter?: any): Promise<ResourceCategory | null> {
    return ResourceCategory.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<ResourceCategory> {
    return ResourceCategory.findById(id, filter);
  }

  static async create(
    data: Partial<ResourceCategory>,
  ): Promise<ResourceCategory> {
    return ResourceCategory.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<ResourceCategory>,
  ): Promise<ResourceCategory> {
    return ResourceCategory.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return ResourceCategory.deleteById(id);
  }

  async dataForCopy(): Promise<any> {
    const instance = new ResourceCategory(this.getRawValue());
    return instance.dataForCopy();
  }
}

export class ResourceCategoryFormArray extends PersistedFormArray {
  readonly entityName = 'ResourceCategory' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(resourceCategorySchema, ResourceCategory, options);
  }

  protected override createItemGroup() {
    return new ResourceCategoryFormGroup();
  }

  get array(): ResourceCategory[] {
    return this.controls.map(
      (group: ResourceCategoryFormGroup) => group.object,
    );
  }
}

export class ResourceCategoryPersistedArray extends PersistedArray<ResourceCategory> {
  readonly entityName = 'ResourceCategory' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ResourceCategory, options);
  }
}
